import { useApolloClient } from '@apollo/client';
import { message } from 'antd';
import { Formik } from 'formik';
import { isNil, omit } from 'lodash/fp';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
    type CreateCompanyMutation,
    type CreateCompanyMutationVariables,
    CreateCompanyDocument,
} from '../../../api/mutations/createCompany';
import {
    type CreateEdmEmailSocialMediaMutation,
    type CreateEdmEmailSocialMediaMutationVariables,
    CreateEdmEmailSocialMediaDocument,
} from '../../../api/mutations/createEdmEmailSocialMedia';
import useLoadingButton from '../../../components/button/useLoadingButton';
import Form from '../../../components/fields/Form';
import FormAutoTouch from '../../../components/FormAutoTouch';
import ConsolePageWithHeader from '../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import useHandleError from '../../../utilities/useHandleError';
import CompanyForm, {
    useCompanyFormValidator,
    defaultValues,
    type CompanyFormValues,
} from '../CompanyDetailsPage/CompanyForm';
import useHandleCompanyAssets from '../CompanyDetailsPage/useHandleCompanyAssets';
import useHandleEdmEmailSocialMediaAsset from '../CompanyDetailsPage/useHandleEdmEmailSocialMediaAsset';

const AddCompanyPage = () => {
    const { t } = useTranslation(['companyDetails']);
    const navigate = useNavigate();
    const validate = useCompanyFormValidator();
    const { setLoading, LoadingButton } = useLoadingButton();

    const apolloClient = useApolloClient();

    const handleCompanyAssets = useHandleCompanyAssets();
    const handleEdmEmailSocialMediaAsset = useHandleEdmEmailSocialMediaAsset();

    const onSubmit = useHandleError<CompanyFormValues>(
        async values => {
            const { favicon, logo, font, mfaSettings, fontBold, edmEmailFooter, phone, ...settings } = values;
            const { socialMedia, phoneIcon, emailIcon, arrowIcon, ...edmEmailFooterSettings } = edmEmailFooter;

            setLoading(true);

            // submitting message
            message.loading({
                content: t('companyDetails:messages.createSubmitting'),
                key: 'primary',
                duration: 0,
            });

            const vatRateSettings = settings.vatRateSettings?.vatRateTable?.length ? settings.vatRateSettings : null;

            // submit creation
            const { data } = await apolloClient.mutate<CreateCompanyMutation, CreateCompanyMutationVariables>({
                mutation: CreateCompanyDocument,
                variables: {
                    settings: {
                        ...settings,
                        phone: isNil(phone?.prefix) || isNil(phone?.value) ? null : phone,
                        ...(mfaSettings.enabled && { mfaSettings: { type: mfaSettings.type } }),
                        edmEmailFooter: edmEmailFooterSettings,
                        vatRateSettings,
                    },
                },
            });

            try {
                // handle company asset upload
                // if upload fails, we still redirect to edit page to avoid duplicate create
                await handleCompanyAssets(data.company.id, values);
            } catch (error) {
                console.warn(error);
                navigate(`/admin/companies/${data.company.id}`);
            }

            const promises = (socialMedia || [])
                .map(async media => {
                    const mediaInput = omit('icon', media);

                    if (!mediaInput.url) {
                        return null;
                    }

                    return apolloClient
                        .mutate<CreateEdmEmailSocialMediaMutation, CreateEdmEmailSocialMediaMutationVariables>({
                            mutation: CreateEdmEmailSocialMediaDocument,
                            variables: {
                                companyId: data.company.id,
                                socialMediaInput: mediaInput,
                            },
                        })
                        .then(createMedia =>
                            handleEdmEmailSocialMediaAsset(createMedia.data.createEdmEmailSocialMedia.id, media.icon)
                        );
                })
                .filter(Boolean);

            await Promise.all(promises);

            // inform about success
            message.success({
                content: t('companyDetails:messages.createSuccessful'),
                key: 'primary',
            });

            // then go back to list
            navigate(`/admin/companies`);
        },
        [apolloClient, handleCompanyAssets, handleEdmEmailSocialMediaAsset, navigate, setLoading, t],
        undefined,
        () => {
            setLoading(false);
        }
    );

    return (
        <ConsolePageWithHeader
            footer={[
                <LoadingButton key="submit" form="companyForm" htmlType="submit" type="primary">
                    {t('companyDetails:actions.create')}
                </LoadingButton>,
            ]}
            onBack={() => navigate('/admin/companies')}
            title={t('companyDetails:addTitle')}
        >
            <Formik initialValues={defaultValues} onSubmit={onSubmit} validate={validate}>
                {({ handleSubmit }) => (
                    <Form id="companyForm" name="companyForm" onSubmitCapture={handleSubmit}>
                        <FormAutoTouch />
                        <CompanyForm />
                    </Form>
                )}
            </Formik>
        </ConsolePageWithHeader>
    );
};

export default AddCompanyPage;
